// =============================================================================
// API Configuration - Industry Standard
// =============================================================================
//
// Environment Files (Create React App standard):
//   .env.development  → Used by: npm start (local development)
//   .env.production   → Used by: npm run build (S3/production deployment)
//
// How it works:
//   - Local (npm start):     Reads .env.development → localhost:5000
//   - Production (npm build): Reads .env.production → Your AWS backend URL
//
// =============================================================================

/**
 * Determines if we're in a browser environment
 */
const isBrowser = typeof window !== 'undefined';

/**
 * API Base URL
 * 
 * Reads from environment files:
 * - Development: .env.development (REACT_APP_API_URL=http://localhost:5000)
 * - Production:  .env.production  (REACT_APP_API_URL=https://your-backend.com)
 */
export const API_BASE_URL: string = (() => {
  // Environment variable from .env.development or .env.production
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Production fallback: relative URLs (only works if backend serves the frontend from same origin)
  // For S3/CloudFront: MUST set REACT_APP_API_URL in frontend/.env.production
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  
  // Development fallback
  return 'http://localhost:5000';
})();

/**
 * WebSocket URL
 * 
 * WebSockets need absolute URLs for production
 */
export const WS_URL: string = (() => {
  // Explicit env var from .env files
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  
  // If API URL is set, derive WS URL from it
  if (process.env.REACT_APP_API_URL) {
    const apiUrl = process.env.REACT_APP_API_URL;
    // Convert http(s) to ws(s)
    return apiUrl.replace(/^http/, 'ws');
  }
  
  // Production: Use current origin (for CloudFront routing)
  if (process.env.NODE_ENV === 'production' && isBrowser) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }
  
  // Development fallback
  return 'http://localhost:5000';
})();

/**
 * Build full API URL from path
 * 
 * @example
 * apiUrl('/api/flows') // Production: '/api/flows', Dev: 'http://localhost:5000/api/flows'
 */
export const apiUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

/**
 * Get WebSocket URL for socket.io connections
 */
export const getWsUrl = (): string => WS_URL;

/**
 * Resolve a backend-served asset URL for S3/CloudFront deployments.
 * 
 * When the frontend is hosted on S3/CloudFront, relative paths like `/svg/logo.png`
 * resolve against the CDN origin instead of the backend server. This function
 * prepends API_BASE_URL so the browser fetches the asset from the backend.
 * 
 * @param path - Relative asset path (e.g., '/svg/logo-123.png') or full URL
 * @returns Full URL pointing to the backend server
 * 
 * @example
 * resolveAssetUrl('/svg/logo.png')   // Dev: 'http://localhost:5000/svg/logo.png'
 * resolveAssetUrl('/svg/logo.png')   // Prod: 'https://api.example.com/svg/logo.png'
 * resolveAssetUrl('https://cdn/img') // 'https://cdn/img' (already absolute, returned as-is)
 * resolveAssetUrl('')                // '' (empty, returned as-is)
 */
export const resolveAssetUrl = (path: string): string => {
  // Return as-is if empty, data URI, or already absolute URL
  if (!path || path.startsWith('data:') || path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:')) {
    return path;
  }
  // Relative path like /svg/... → prepend backend base URL
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

/**
 * Debug: Get current configuration
 */
export const getApiConfig = () => ({
  API_BASE_URL,
  WS_URL,
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL || '(not set)',
});

// Log config in development for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('[API Config]', getApiConfig());
}
